import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Send, Loader2, Zap, ArrowLeft, ShoppingCart, Crown, FileText, Mic, PenTool, Target, Sparkles, BookOpen, X, Plus, Minus, CreditCard, AlertTriangle } from "lucide-react";
import axios from "axios";
import { getMetaAttributionPayload } from "@/utils/metaPixel";
import "@/styles/landing.css";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CARA_AVATAR = "https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=100&h=100&fit=crop";

const SKILL_ICONS = {
  FileText: FileText,
  Mic: Mic,
  PenTool: PenTool,
  Target: Target,
};

const SKILL_SUGGESTIONS = {
  resume_writing: [
    "How do I optimize my resume for ATS systems?",
    "Write better bullet points for my experience",
    "What keywords should I add for my target role?",
    "Review my resume sections and suggest improvements",
  ],
  interview_prep: [
    "Help me prepare a STAR answer for leadership",
    "What salary should I negotiate for my role?",
    "Give me a script to counter a low offer",
    "Top behavioral questions for my target role",
  ],
  cover_letter: [
    "Write a compelling opening for my cover letter",
    "Help me craft a cover letter using AIDA framework",
    "Write a cold email to a hiring manager",
    "How to make my application stand out",
  ],
  career_strategy: [
    "Optimize my LinkedIn profile for recruiters",
    "Build a 30-day job search strategy",
    "How to network effectively for hidden jobs",
    "Create my personal brand positioning",
  ],
  general: [
    "How should I negotiate salary?",
    "Prepare me for interviews",
    "What skills should I learn next?",
    "Review my career path",
  ]
};

// Role-specific suggestions that will be shown based on target role
const getRoleSuggestions = (role) => {
  if (!role) return [];
  const roleLower = role.toLowerCase();

  // AI/ML roles
  if (roleLower.includes('ai') || roleLower.includes('ml') || roleLower.includes('machine learning') || roleLower.includes('data scientist')) {
    return [
      `What are the must-have skills for ${role}?`,
      `Help me prepare for a ${role} technical interview`,
      `What projects should I showcase for ${role}?`,
      `What salary range should I expect for ${role}?`,
    ];
  }

  // Data roles
  if (roleLower.includes('data analyst') || roleLower.includes('business analyst') || roleLower.includes('analytics')) {
    return [
      `What SQL skills do I need for ${role}?`,
      `How do I showcase my analytical skills for ${role}?`,
      `What certifications help for ${role}?`,
      `Help me with case study prep for ${role}`,
    ];
  }

  // Engineering roles
  if (roleLower.includes('engineer') || roleLower.includes('developer') || roleLower.includes('full stack')) {
    return [
      `What tech stack should I highlight for ${role}?`,
      `How do I prepare for coding interviews?`,
      `What projects impress recruiters for ${role}?`,
      `How do I negotiate salary for ${role}?`,
    ];
  }

  // Management/Operations roles
  if (roleLower.includes('manager') || roleLower.includes('operations') || roleLower.includes('lead')) {
    return [
      `How do I showcase leadership experience for ${role}?`,
      `What metrics should I highlight for ${role}?`,
      `Help me prepare for behavioral interviews`,
      `What management frameworks should I know?`,
    ];
  }

  // Default role-specific suggestions
  return [
    `What are the key skills needed for ${role}?`,
    `How do I tailor my resume for ${role}?`,
    `What interview questions should I expect for ${role}?`,
    `What salary should I target for ${role}?`,
  ];
};

export default function CompanionPage() {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [credits, setCredits] = useState(0);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [hasPass, setHasPass] = useState(false);
  const [skills, setSkills] = useState([]);
  const [activeSkill, setActiveSkill] = useState("");
  const [showBuyModal, setShowBuyModal] = useState(false);
  const [buyCredits, setBuyCreditsCount] = useState(25);
  const [buyLoading, setBuyLoading] = useState(false);
  const [targetRole, setTargetRole] = useState("");
  const [hasResume, setHasResume] = useState(false);
  const [resumeName, setResumeName] = useState("");
  const messagesEnd = useRef(null);
  const navigate = useNavigate();
  const { getHeaders, user } = useAuth();

  const fetchData = useCallback(async () => {
    try {
      const [subRes, histRes, skillsRes] = await Promise.allSettled([
        axios.get(`${API}/user/subscription`, { headers: getHeaders() }),
        axios.get(`${API}/companion/history`, { headers: getHeaders() }),
        axios.get(`${API}/companion/skills`)
      ]);
      if (subRes.status === "fulfilled") {
        setHasPass(subRes.value.data.has_season_pass);
        setCredits(subRes.value.data.ai_credits || 0);
        setTargetRole(subRes.value.data.target_role || "");
      }
      if (histRes.status === "fulfilled") {
        setMessages(histRes.value.data.messages || []);
        setCredits(histRes.value.data.credits || 0);
        setHasResume(histRes.value.data.has_resume || false);
        setResumeName(histRes.value.data.resume_name || "");
        if (histRes.value.data.target_role) {
          setTargetRole(histRes.value.data.target_role);
        }
      }
      if (skillsRes.status === "fulfilled") {
        setSkills(skillsRes.value.data.skills || []);
      }
    } catch (e) { /* ok */ }
    setLoading(false);
  }, [getHeaders]);

  // Get role from user context as fallback
  useEffect(() => {
    if (user?.target_role && !targetRole) {
      setTargetRole(user.target_role);
    }
  }, [user, targetRole]);

  useEffect(() => {
    if (!user) { navigate("/auth?return=/companion"); return; }
    fetchData();
  }, [user, navigate, fetchData]);

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!input.trim() || sending) return;
    if (credits <= 0) { toast.error("No credits remaining. Purchase more to continue."); return; }

    const userMsg = input.trim();
    setInput("");
    setMessages(prev => [...prev, { role: "user", content: userMsg, created_at: new Date().toISOString() }]);
    setSending(true);

    try {
      const payload = { message: userMsg };
      if (activeSkill) payload.skill = activeSkill;
      const res = await axios.post(`${API}/companion/message`, payload, { headers: { ...getHeaders(), "Content-Type": "application/json" } });
      setMessages(prev => [...prev, {
        role: "assistant",
        content: res.data.reply,
        created_at: new Date().toISOString(),
        skill: res.data.active_skill,
        skill_name: res.data.skill_name
      }]);
      setCredits(res.data.credits);
    } catch (err) {
      if (err.response?.status === 402) {
        toast.error("No AI credits remaining");
      } else {
        toast.error(err.response?.data?.detail || "Failed to get response");
      }
    } finally {
      setSending(false);
    }
  };

  const handleSuggestion = (q) => {
    setInput(q);
  };

  const pricePerCredit = 2; // ₹2 per credit
  const roundedCredits = Math.max(25, Math.ceil(buyCredits / 5) * 5);
  const totalPrice = (roundedCredits / 5) * 10;

  const handleBuyCredits = async () => {
    setBuyLoading(true);
    try {
      const res = await axios.post(`${API}/companion/buy-credits`, {
        origin_url: window.location.origin,
        credits: roundedCredits,
        ...getMetaAttributionPayload(),
      }, { headers: { ...getHeaders(), "Content-Type": "application/json" } });
      window.location.href = res.data.url;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to initiate purchase");
      setBuyLoading(false);
    }
  };

  useEffect(() => { messagesEnd.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  // Get role-specific suggestions if available, otherwise use skill-based suggestions
  const roleSuggestions = getRoleSuggestions(targetRole);
  const currentSuggestions = roleSuggestions.length > 0 && !activeSkill
    ? roleSuggestions
    : (SKILL_SUGGESTIONS[activeSkill] || SKILL_SUGGESTIONS.general);

  const getSkillBadge = (skillKey, skillName) => {
    const skill = skills.find(s => s.key === skillKey);
    if (!skill && skillKey === "general") return null;
    if (!skill) return null;
    return (
      <span
        className="inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full mt-1"
        style={{ backgroundColor: `${skill.color}15`, color: skill.color, border: `1px solid ${skill.color}30` }}
      >
        <BookOpen className="w-2.5 h-2.5" />
        {skill.name}
      </span>
    );
  };

  if (loading) return (
    <div className="ap-root min-h-screen pt-24 flex items-center justify-center">
      <Loader2 className="w-8 h-8 animate-spin" style={{ color: "var(--ap-violet)" }} />
    </div>
  );

  if (!hasPass) {
    return (
      <div className="ap-root min-h-screen pt-24 flex items-center justify-center px-4" data-testid="companion-locked">
        <div className="ap-card ap-rise rounded-[24px] max-w-md w-full text-center p-10 relative overflow-hidden">
          <div className="absolute top-0 left-0 right-0 h-1.5" style={{ background: "var(--ap-fire)" }} />
          <div className="w-16 h-16 rounded-2xl grid place-items-center mx-auto mb-5" style={{ background: "var(--ap-violet-soft)" }}>
            <Crown className="w-8 h-8" style={{ color: "var(--ap-violet)" }} />
          </div>
          <h2 className="ap-display text-[22px] font-extrabold text-[var(--ap-ink)] mb-2">Full Career OS Required</h2>
          <p className="text-[var(--ap-muted)] text-sm leading-relaxed mb-7">Cara - Career Guide is available with the Full Career OS. Get personalized career coaching, salary negotiation scripts, and interview prep.</p>
          <button onClick={() => navigate("/season-pass")} className="ap-btn ap-btn-fire ap-btn-lg w-full uppercase tracking-wide" data-testid="companion-upgrade-btn">
            GET FULL CAREER OS
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="ap-root min-h-screen pt-24 flex flex-col" data-testid="companion-page">
      <div className="flex-1 flex flex-col max-w-3xl mx-auto w-full px-3 sm:px-4">
        {/* Header */}
        <div className="ap-glass rounded-[20px] px-4 py-3.5 flex items-center justify-between sticky top-16 z-20 mt-2">
          <div className="flex items-center gap-3.5">
            <button onClick={() => navigate("/dashboard")} className="w-9 h-9 rounded-full grid place-items-center text-[var(--ap-muted)] hover:text-[var(--ap-ink)] hover:bg-[var(--ap-canvas-2)] transition-colors bg-transparent border-0 cursor-pointer shrink-0" data-testid="companion-back-btn">
              <ArrowLeft className="w-5 h-5" />
            </button>
            <span className="relative shrink-0">
              <img src={CARA_AVATAR} alt="Cara" className="w-11 h-11 rounded-full object-cover ring-2 ring-white" style={{ boxShadow: "0 0 0 2px var(--ap-violet)" }} />
              <span className="absolute bottom-0 right-0 w-3 h-3 rounded-full border-2 border-white" style={{ background: "var(--ap-green)" }} />
            </span>
            <div>
              <h2 className="ap-display text-[15px] font-extrabold text-[var(--ap-ink)] flex items-center gap-2 leading-tight" data-testid="companion-title">
                Cara - Career Guide
              </h2>
              <span className="text-[var(--ap-muted)] text-xs">Expert career coaching powered by AI</span>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {targetRole && (
              <span className="hidden sm:inline-flex ap-chip" style={{ background: "rgba(22,163,74,.12)", borderColor: "transparent", color: "var(--ap-green)", fontSize: 12, padding: "5px 11px" }} data-testid="target-role-badge">
                <Target className="w-3.5 h-3.5" />{targetRole}
              </span>
            )}
            <span className="ap-chip" style={{ background: "var(--ap-violet-soft)", borderColor: "transparent", color: "var(--ap-violet)", fontSize: 12, padding: "5px 11px", fontWeight: 700 }} data-testid="credits-badge">
              <Zap className="w-3.5 h-3.5" />{credits} credits
            </span>
            {credits <= 3 && (
              <button onClick={() => setShowBuyModal(true)} className="ap-btn ap-btn-pri shrink-0" style={{ fontSize: 12, padding: "6px 12px" }} data-testid="buy-credits-btn">
                <ShoppingCart className="w-3.5 h-3.5" /> Buy Credits
              </button>
            )}
          </div>
        </div>

        {/* Context Banners */}
        <div className="mt-3 space-y-2">
          {/* Target Role Banner */}
          {targetRole && (
            <div className="rounded-2xl px-4 py-2.5" style={{ background: "rgba(22,163,74,.08)", border: "1px solid rgba(22,163,74,.2)" }}>
              <div className="flex items-center gap-2 text-[13px]">
                <Target className="w-4 h-4 text-[var(--ap-green)] shrink-0" />
                <span className="text-[var(--ap-ink-2)]">Advice tailored for:</span>
                <span className="font-bold text-[var(--ap-green)]">{targetRole}</span>
              </div>
            </div>
          )}

          {/* Resume Status Banner */}
          {hasResume ? (
            <div className="rounded-2xl px-4 py-2.5" style={{ background: "var(--ap-violet-soft)", border: "1px solid transparent" }}>
              <div className="flex items-center gap-2 text-[13px]">
                <FileText className="w-4 h-4 text-[var(--ap-violet)] shrink-0" />
                <span className="text-[var(--ap-ink-2)]">AI referencing your resume:</span>
                <span className="font-bold text-[var(--ap-violet)] truncate">{resumeName}</span>
              </div>
            </div>
          ) : (
            <div className="rounded-2xl px-4 py-2.5" style={{ background: "rgba(255,107,53,.08)", border: "1px solid rgba(255,107,53,.22)" }}>
              <div className="flex items-center justify-between gap-3 text-[13px]">
                <div className="flex items-center gap-2 min-w-0">
                  <AlertTriangle className="w-4 h-4 text-[var(--ap-orange)] shrink-0" />
                  <span className="text-[var(--ap-ink-2)] truncate">No resume uploaded — advice will be generic</span>
                </div>
                <button
                  onClick={() => navigate("/intake")}
                  className="text-[var(--ap-orange)] font-semibold hover:underline cursor-pointer bg-transparent border-0 shrink-0 whitespace-nowrap"
                  data-testid="upload-resume-link"
                >
                  Upload Resume →
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Skill Selector */}
        <div className="mt-3 pb-1">
          <div className="flex items-center gap-2 overflow-x-auto pb-1.5 scrollbar-hide">
            <button
              onClick={() => setActiveSkill("")}
              className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full text-[12.5px] font-semibold whitespace-nowrap transition-all cursor-pointer border"
              style={!activeSkill
                ? { background: "var(--ap-ink)", color: "#fff", borderColor: "var(--ap-ink)" }
                : { background: "#fff", color: "var(--ap-muted)", borderColor: "var(--ap-line)" }}
              data-testid="skill-auto"
            >
              <Sparkles className="w-3.5 h-3.5" />
              Auto-Detect
            </button>
            {skills.map(skill => {
              const IconComp = SKILL_ICONS[skill.icon] || Sparkles;
              const isActive = activeSkill === skill.key;
              return (
                <button
                  key={skill.key}
                  onClick={() => setActiveSkill(isActive ? "" : skill.key)}
                  className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full text-[12.5px] font-semibold whitespace-nowrap transition-all cursor-pointer border"
                  style={isActive ? {
                    backgroundColor: `${skill.color}18`,
                    borderColor: `${skill.color}40`,
                    color: skill.color
                  } : { background: "#fff", color: "var(--ap-muted)", borderColor: "var(--ap-line)" }}
                  data-testid={`skill-${skill.key}`}
                >
                  <IconComp className="w-3.5 h-3.5" />
                  {skill.name}
                </button>
              );
            })}
          </div>
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto py-6 space-y-6" data-testid="companion-messages">
          {messages.length === 0 && (
            <div className="text-center py-6">
              <img src={CARA_AVATAR} alt="Cara" className="w-20 h-20 rounded-full mx-auto mb-5 object-cover ring-2 ring-white" style={{ boxShadow: "0 0 0 3px var(--ap-violet), var(--ap-glow)" }} />
              <p className="ap-display text-[22px] font-extrabold text-[var(--ap-ink)] mb-2">
                {targetRole ? <>Hi! I'm <span className="ap-grad-text">Cara</span>, your {targetRole} Career Guide</> : <>Hi! I'm <span className="ap-grad-text">Cara</span>, your Career Guide</>}
              </p>
              <p className="text-[var(--ap-muted)] text-[14.5px] leading-relaxed max-w-md mx-auto mb-4">
                {targetRole
                  ? `Get personalized advice for your ${targetRole} career journey. All suggestions are tailored to help you land your dream role.`
                  : 'I can help you with resume optimization, interview prep, salary negotiation, and career strategy.'
                }
              </p>

              {/* Target Role Highlight */}
              {targetRole && (
                <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-6" style={{ background: "rgba(22,163,74,.1)", border: "1px solid rgba(22,163,74,.3)" }}>
                  <Target className="w-4 h-4 text-[var(--ap-green)]" />
                  <span className="text-sm font-semibold text-[var(--ap-green)]">{targetRole}</span>
                </div>
              )}

              {/* Skill Cards */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-lg mx-auto mt-2 mb-8">
                {skills.map(skill => {
                  const IconComp = SKILL_ICONS[skill.icon] || Sparkles;
                  return (
                    <button
                      key={skill.key}
                      onClick={() => setActiveSkill(skill.key)}
                      className="ap-card ap-pop flex items-start gap-3 p-4 cursor-pointer text-left"
                      data-testid={`skill-card-${skill.key}`}
                    >
                      <div className="w-10 h-10 rounded-xl grid place-items-center shrink-0" style={{ backgroundColor: `${skill.color}15` }}>
                        <IconComp className="w-5 h-5" style={{ color: skill.color }} />
                      </div>
                      <div>
                        <div className="ap-display text-[14px] font-bold text-[var(--ap-ink)]">{skill.name}</div>
                        <div className="text-[var(--ap-muted)] text-[11.5px] leading-snug mt-1">{skill.description}</div>
                      </div>
                    </button>
                  );
                })}
              </div>

              {/* Quick Prompts - Role Specific */}
              <p className="ap-eyebrow mb-4">
                {targetRole ? `Questions for ${targetRole}` : 'Quick Prompts'}
              </p>
              <div className="flex flex-wrap gap-2 justify-center">
                {currentSuggestions.map(q => (
                  <button key={q} onClick={() => handleSuggestion(q)} className="px-3.5 py-2 text-[13px] font-medium bg-white border border-[var(--ap-line)] text-[var(--ap-ink-2)] rounded-full hover:border-[var(--ap-violet)] hover:text-[var(--ap-violet)] transition-all cursor-pointer" data-testid={`suggestion-${q.slice(0,15).replace(/\s/g,'-').toLowerCase()}`}>
                    {q}
                  </button>
                ))}
              </div>
            </div>
          )}

          {messages.map((msg, i) => (
            <div key={i} className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}>
              {msg.role === "assistant" && (
                <img src={CARA_AVATAR} alt="Cara" className="w-9 h-9 rounded-full mr-3 mt-1 shrink-0 object-cover ring-2 ring-white" style={{ boxShadow: "0 0 0 1.5px var(--ap-violet)" }} />
              )}
              <div
                className="p-4 max-w-[85%]"
                style={msg.role === "user"
                  ? { background: "var(--ap-violet)", color: "#fff", borderRadius: "18px 18px 4px 18px", boxShadow: "var(--ap-shadow-sm)" }
                  : { background: "var(--ap-card)", color: "var(--ap-ink)", border: "1px solid var(--ap-line)", borderRadius: "4px 18px 18px 18px", boxShadow: "var(--ap-shadow-sm)" }}
              >
                <div className="text-[14px] leading-relaxed whitespace-pre-wrap">
                  {msg.content.split('\n').map((line, j) => {
                    if (line.startsWith('### ')) return <h4 key={j} className={`font-bold text-sm mt-3 mb-1 ${msg.role === "user" ? "text-white" : "text-[var(--ap-ink)]"}`}>{line.replace('### ', '')}</h4>;
                    if (line.startsWith('## ')) return <h3 key={j} className={`font-bold text-base mt-3 mb-1 ${msg.role === "user" ? "text-white" : "text-[var(--ap-ink)]"}`}>{line.replace('## ', '')}</h3>;
                    if (line.startsWith('# ')) return <h2 key={j} className={`font-bold text-lg mt-3 mb-1 ${msg.role === "user" ? "text-white" : "text-[var(--ap-ink)]"}`}>{line.replace('# ', '')}</h2>;
                    if (line.startsWith('**') && line.endsWith('**')) return <p key={j} className={`font-bold mt-2 ${msg.role === "user" ? "text-white" : "text-[var(--ap-ink)]"}`}>{line.replace(/\*\*/g, '')}</p>;
                    if (line.startsWith('- ') || line.startsWith('• ')) return <li key={j} className={`ml-4 list-disc ${msg.role === "user" ? "text-white/95" : "text-[var(--ap-ink-2)]"}`}>{line.replace(/^[-•]\s/, '')}</li>;
                    if (line.match(/^\d+\.\s/)) return <li key={j} className={`ml-4 list-decimal ${msg.role === "user" ? "text-white/95" : "text-[var(--ap-ink-2)]"}`}>{line.replace(/^\d+\.\s/, '')}</li>;
                    if (line.trim() === '') return <br key={j} />;
                    return <p key={j} className={msg.role === "user" ? "text-white" : "text-[var(--ap-ink-2)]"}>{line}</p>;
                  })}
                </div>
                {msg.role === "assistant" && msg.skill && msg.skill !== "general" && (
                  <div className="mt-2 pt-2 border-t border-[var(--ap-line)]">
                    {getSkillBadge(msg.skill, msg.skill_name)}
                  </div>
                )}
              </div>
            </div>
          ))}

          {sending && (
            <div className="flex items-start gap-3">
              <img src={CARA_AVATAR} alt="Cara" className="w-9 h-9 rounded-full shrink-0 object-cover ring-2 ring-white" style={{ boxShadow: "0 0 0 1.5px var(--ap-violet)" }} />
              <div className="p-4" style={{ background: "var(--ap-card)", border: "1px solid var(--ap-line)", borderRadius: "4px 18px 18px 18px", boxShadow: "var(--ap-shadow-sm)" }}>
                <div className="flex items-center gap-2">
                  <div className="flex gap-1.5">
                    <span className="typing-dot w-2 h-2 rounded-full" style={{ background: "var(--ap-violet)" }} />
                    <span className="typing-dot w-2 h-2 rounded-full" style={{ background: "var(--ap-violet)" }} />
                    <span className="typing-dot w-2 h-2 rounded-full" style={{ background: "var(--ap-violet)" }} />
                  </div>
                  <span className="text-[var(--ap-muted)] text-xs">Cara is thinking...</span>
                </div>
              </div>
            </div>
          )}
          <div ref={messagesEnd} />
        </div>

        {/* Input */}
        <div className="sticky bottom-0 py-4" style={{ background: "linear-gradient(to top, var(--ap-canvas) 70%, transparent)" }}>
          {activeSkill && messages.length > 0 && (
            <div className="flex items-center gap-2 mb-3">
              <span className="text-[11px] text-[var(--ap-muted)] font-medium">Active:</span>
              {(() => {
                const skill = skills.find(s => s.key === activeSkill);
                if (!skill) return null;
                const IconComp = SKILL_ICONS[skill.icon] || Sparkles;
                return (
                  <span className="inline-flex items-center gap-1 text-[11px] font-semibold px-2.5 py-1 rounded-full" style={{ backgroundColor: `${skill.color}15`, color: skill.color, border: `1px solid ${skill.color}30` }}>
                    <IconComp className="w-2.5 h-2.5" />
                    {skill.name}
                    <button onClick={() => setActiveSkill("")} className="ml-1 hover:opacity-70 cursor-pointer bg-transparent border-0 p-0" style={{ color: skill.color }}>×</button>
                  </span>
                );
              })()}
            </div>
          )}
          {credits <= 0 ? (
            <div className="ap-card flex items-center justify-between px-4 py-3.5" style={{ borderColor: "var(--ap-violet)" }}>
              <span className="text-[var(--ap-ink-2)] text-sm font-medium">No credits remaining</span>
              <button onClick={() => setShowBuyModal(true)} className="ap-btn ap-btn-fire ap-btn-md" data-testid="buy-credits-bottom-btn">
                Buy Credits
              </button>
            </div>
          ) : (
            <form onSubmit={sendMessage} className="ap-card flex gap-2 p-2 items-center" style={{ borderRadius: "999px" }}>
              <Input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder={activeSkill ? `Ask Cara about ${skills.find(s => s.key === activeSkill)?.name?.toLowerCase() || 'career'}...` : "Ask Cara about your career..."}
                className="flex-1 bg-transparent border-0 shadow-none text-[var(--ap-ink)] placeholder:text-[var(--ap-muted)] h-11 focus-visible:ring-0 focus-visible:ring-offset-0 px-3"
                data-testid="companion-input"
                disabled={sending}
              />
              <button type="submit" disabled={sending || !input.trim()} className="ap-btn ap-btn-pri shrink-0 disabled:opacity-50" style={{ width: 44, height: 44, padding: 0, borderRadius: "999px" }} data-testid="companion-send-btn">
                <Send className="w-5 h-5" />
              </button>
            </form>
          )}
        </div>
      </div>

      {/* Buy Credits Modal */}
      {showBuyModal && (
        <div className="fixed inset-0 flex items-center justify-center p-4" style={{ background: "rgba(12,17,29,0.55)", zIndex: 50, backdropFilter: "blur(3px)" }} data-testid="buy-credits-modal" onClick={() => setShowBuyModal(false)}>
          <div className="ap-card ap-rise w-full max-w-sm p-0 overflow-hidden" onClick={e => e.stopPropagation()}>
            <div className="h-1.5" style={{ background: "var(--ap-fire)" }} />
            {/* Modal Header */}
            <div className="flex items-center justify-between px-5 py-4 border-b border-[var(--ap-line)]">
              <div className="flex items-center gap-2.5">
                <div className="w-9 h-9 rounded-xl grid place-items-center" style={{ background: "var(--ap-fire)" }}>
                  <Zap className="w-4 h-4 text-white" />
                </div>
                <div>
                  <h3 className="ap-display text-[15px] font-extrabold text-[var(--ap-ink)] m-0">Buy AI Credits</h3>
                  <p className="text-[var(--ap-muted)] text-[11px] m-0">₹10 for every 5 credits</p>
                </div>
              </div>
              <button onClick={() => setShowBuyModal(false)} className="text-[var(--ap-muted)] hover:text-[var(--ap-ink)] bg-transparent border-0 cursor-pointer p-1" data-testid="close-buy-modal">
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Credit Selector */}
            <div className="px-5 py-5">
              <label className="text-[var(--ap-muted)] text-[13px] font-medium mb-3 block">How many credits do you need?</label>
              <div className="flex items-center gap-3 mb-4">
                <button
                  onClick={() => setBuyCreditsCount(prev => Math.max(25, prev - 5))}
                  className="w-11 h-11 rounded-xl grid place-items-center text-[var(--ap-ink)] transition-all cursor-pointer disabled:opacity-30 shrink-0"
                  style={{ background: "var(--ap-canvas)", border: "1px solid var(--ap-line)" }}
                  disabled={buyCredits <= 25}
                  data-testid="credits-minus"
                >
                  <Minus className="w-4 h-4" />
                </button>
                <div className="flex-1">
                  <input
                    type="number"
                    value={buyCredits}
                    onChange={e => {
                      const val = parseInt(e.target.value) || 25;
                      setBuyCreditsCount(Math.min(500, Math.max(25, val)));
                    }}
                    className="ap-display w-full text-center text-2xl font-extrabold text-[var(--ap-ink)] rounded-xl py-2.5 focus:outline-none"
                    style={{ background: "var(--ap-canvas)", border: "1px solid var(--ap-line)" }}
                    onFocus={(e) => e.target.style.borderColor = "var(--ap-violet)"}
                    onBlur={(e) => e.target.style.borderColor = "var(--ap-line)"}
                    min="25"
                    max="500"
                    data-testid="credits-input"
                  />
                </div>
                <button
                  onClick={() => setBuyCreditsCount(prev => Math.min(500, prev + 5))}
                  className="w-11 h-11 rounded-xl grid place-items-center text-[var(--ap-ink)] transition-all cursor-pointer disabled:opacity-30 shrink-0"
                  style={{ background: "var(--ap-canvas)", border: "1px solid var(--ap-line)" }}
                  disabled={buyCredits >= 500}
                  data-testid="credits-plus"
                >
                  <Plus className="w-4 h-4" />
                </button>
              </div>

              {/* Quick Select */}
              <div className="flex gap-2 mb-5">
                {[25, 50, 100, 200, 500].map(n => (
                  <button
                    key={n}
                    onClick={() => setBuyCreditsCount(n)}
                    className="flex-1 py-2 rounded-lg text-[12.5px] font-semibold border cursor-pointer transition-all"
                    style={buyCredits === n
                      ? { background: "var(--ap-violet-soft)", borderColor: "var(--ap-violet)", color: "var(--ap-violet)" }
                      : { background: "var(--ap-canvas)", borderColor: "var(--ap-line)", color: "var(--ap-muted)" }}
                    data-testid={`quick-select-${n}`}
                  >
                    {n}
                  </button>
                ))}
              </div>

              {/* Price Breakdown */}
              <div className="rounded-2xl p-4 mb-5" style={{ background: "var(--ap-canvas)", border: "1px solid var(--ap-line)" }}>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-[var(--ap-muted)] text-[13px]">{roundedCredits} credits × ₹{pricePerCredit}</span>
                  <span className="text-[var(--ap-ink)] text-sm font-semibold">₹{totalPrice}</span>
                </div>
                {buyCredits !== roundedCredits && (
                  <p className="text-[var(--ap-violet)] text-[11px] mt-1">Rounded up to {roundedCredits} credits (sold in packs of 5)</p>
                )}
                <div className="flex items-center justify-between pt-2.5 mt-2 border-t border-[var(--ap-line)]">
                  <span className="text-[var(--ap-ink)] text-[13px] font-bold">Total</span>
                  <span className="ap-display text-[var(--ap-ink)] text-xl font-extrabold flex items-center">
                    ₹{totalPrice}
                  </span>
                </div>
              </div>

              {/* Buy Button */}
              <button
                onClick={handleBuyCredits}
                disabled={buyLoading}
                className="ap-btn ap-btn-fire ap-btn-md w-full disabled:opacity-50"
                data-testid="confirm-buy-btn"
              >
                {buyLoading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <>
                    <CreditCard className="w-4 h-4" />
                    Pay ₹{totalPrice} for {roundedCredits} Credits
                  </>
                )}
              </button>

              <p className="text-[var(--ap-muted)] text-[11px] text-center mt-3">Secure payment via Stripe. Credits are non-refundable.</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
